import { useTranslation } from "react-i18next";

import { FilterTable } from "@/features/FilterTable/FilterTable";
import { type Field } from "@/features/FilterTable/types";
import { type DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";

import {
  client,
  filterSchema,
  includeClientFilter,
  names,
  portfolio_table_name,
  static_data_table_name,
  table_name,
  type FilterSchema,
} from "./config";

const portfolioDepFields: readonly DepField<FilterSchema>[] = [
  {
    name: names.portfolioClientContains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: names.portfolioBankNameContains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: names.portfolioVehicleContains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: names.portfolioNameContains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: names.portfolioAccountNameContains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterBankPositions() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    ...(includeClientFilter
      ? [
          {
            name: names.portfolioClientContains,
            label: t(`${table_name}.searchbox.${names.portfolioClientContains}`, { defaultValue: "Client" }),
            type: "asyncAutocomplete" as const,
            base_url: `${client}/${portfolio_table_name}`,
            column_name: "client",
            filters: makeFiltersBuilder<FilterSchema>(
              portfolioDepFields,
              names.portfolioClientContains,
            ),
          },
        ]
      : []),
    {
      name: names.portfolioVehicleContains,
      label: t(`${table_name}.searchbox.${names.portfolioVehicleContains}`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${client}/${portfolio_table_name}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterSchema>(
        portfolioDepFields,
        names.portfolioVehicleContains,
      ),
    },
    {
      name: names.portfolioAccountNameContains,
      label: t(`${table_name}.searchbox.${names.portfolioAccountNameContains}`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${client}/${portfolio_table_name}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterSchema>(
        portfolioDepFields,
        names.portfolioAccountNameContains,
      ),
    },
    {
      name: names.portfolioNameContains,
      label: t(`${table_name}.searchbox.${names.portfolioNameContains}`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${client}/${portfolio_table_name}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterSchema>(
        portfolioDepFields,
        names.portfolioNameContains,
      ),
    },
    {
      name: names.portfolioBankNameContains,
      label: t(`${table_name}.searchbox.${names.portfolioBankNameContains}`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${client}/${portfolio_table_name}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterSchema>(
        portfolioDepFields,
        names.portfolioBankNameContains,
      ),
    },
    {
      name: names.staticDatumNameContains,
      label: t(`${table_name}.searchbox.${names.staticDatumNameContains}`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${client}/${static_data_table_name}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: names.staticDataAssetClassEquals,
      assetSubclassFieldName: names.staticDataAssetSubclassEquals,
      assetClassLabel: t(`${table_name}.searchbox.${names.staticDataAssetClassEquals}`),
      assetSubclassLabel: t(`${table_name}.searchbox.${names.staticDataAssetSubclassEquals}`),
      client,
      staticDataTableName: static_data_table_name,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassFieldType: "select",
    }),
    {
      name: "date",
      type: "dateRange",
      label: t(`${table_name}.columns.date`, { defaultValue: "Date" }),
      from: { name: "date_from" },
      to: { name: "date_to" },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${table_name}.columns.created_at`, { defaultValue: "Created at" }),
      from: { name: "create_from" },
      to: { name: "create_to" },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={client}
      schema={filterSchema}
      tableName={table_name}
      fields={fields}
    />
  );
}
